import { ArcSeg2d } from '@/editor/math/arcSeg2d';
import { Curve2dUtils } from '@/editor/math/Curve2dUtils';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { LineSeg2d } from '@/editor/math/LineSeg2d';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { IShapePoint2d } from '@/editor/types/shape';
import { getPathFromEdges } from '../edge';
import { isArc } from '../arc';

export { toPlainPoint, sampleShapePath, sampleOutlines } from './sampleShape';
export { calAuxiliarySegsCore, shapeSegToPoints, type IAuxiliarySegParams } from './auxiliarySegs';

export function getBaseShape(baseData: {
  start: IPoint2d;
  end: IPoint2d;
  thickness: number;
  center?: IPoint2d;
  clockwise?: boolean;
}) {
  if (!baseData.thickness) {
    return { startShape: [], endShape: [], baseShape: getPathFromEdges([baseData]) };
  }

  // 弧墙
  if (isArc(baseData)) {
    const arc = new ArcSeg2d({
      start: baseData.start,
      end: baseData.end,
      center: baseData.center,
      clockwise: baseData.clockwise
    });

    const startUnitDirection = Point2dUtils.getUnitDirection(baseData.start, arc.center);
    const endUnitDirection = Point2dUtils.getUnitDirection(baseData.end, arc.center);
    const startShape = [
      Point2dUtils.addDistance(baseData.start, baseData.thickness / 2, startUnitDirection),
      Point2dUtils.addDistance(baseData.start, -baseData.thickness / 2, startUnitDirection)
    ];
    const endShape = [
      Point2dUtils.addDistance(baseData.end, baseData.thickness / 2, endUnitDirection),
      Point2dUtils.addDistance(baseData.end, -baseData.thickness / 2, endUnitDirection)
    ];

    return {
      startShape,
      endShape,
      baseShape: getWallDrawShape(baseData, startShape, endShape)
    };
  }

  const mainSeg = new LineSeg2d(baseData.start, baseData.end);
  const { leftCurve, rightCurve } = Curve2dUtils.offsetCurves(mainSeg, baseData.thickness / 2);
  const startShape = [leftCurve.start, rightCurve.start];
  const endShape = [leftCurve.end, rightCurve.end];

  return {
    startShape,
    endShape,
    baseShape: getWallDrawShape(baseData, startShape, endShape)
  };
}

export function reverseShape(shape: IShapePoint2d[]) {
  const newShape: IShapePoint2d[] = [];
  for (let i = shape.length - 1; i >= 0; i--) {
    const prevPoint = shape[i === 0 ? shape.length - 1 : i - 1];

    if (prevPoint.center) {
      newShape.push({ x: shape[i].x, y: shape[i].y, center: prevPoint.center, clockwise: !prevPoint.clockwise });
    } else {
      newShape.push({ x: shape[i].x, y: shape[i].y });
    }
  }
  return newShape;
}

// 根据墙体类型以及起点和终点形状，获取墙体的绘制形状
export function getWallDrawShape(
  wallBaseData: {
    start: IPoint2d;
    end: IPoint2d;
    center?: IPoint2d;
    clockwise?: boolean;
  },
  startShape: IShapePoint2d[],
  endShape: IShapePoint2d[]
): IShapePoint2d[] {
  // ==== 直墙处理 ====
  if (!isArc(wallBaseData)) {
    return [...reverseShape(startShape), ...endShape];
  }

  // ==== 弧墙处理 ====
  // 中心弧
  const coreArc = new ArcSeg2d({
    start: wallBaseData.start,
    end: wallBaseData.end,
    center: wallBaseData.center,
    clockwise: wallBaseData.clockwise
  });
  const { center, clockwise } = coreArc;

  // 求左弧
  const leftArc = new ArcSeg2d({
    start: startShape[0],
    end: endShape[0],
    center,
    clockwise
  });

  // 求右弧
  const rightArc = new ArcSeg2d({
    start: startShape[startShape.length - 1],
    end: endShape[endShape.length - 1],
    center,
    clockwise
  });

  const reverseStartShape = reverseShape(startShape);
  endShape = endShape.map((point) => ({ ...point, center: point.center, clockwise: point.clockwise })); // 克隆一份新数据
  reverseStartShape[reverseStartShape.length - 1].center = leftArc.center;
  reverseStartShape[reverseStartShape.length - 1].clockwise = leftArc.clockwise;
  endShape[endShape.length - 1].center = rightArc.center;
  endShape[endShape.length - 1].clockwise = !rightArc.clockwise;

  return [...reverseStartShape, ...endShape];
}
